import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { candidatePortalApi, clearPortalToken } from './candidate-portal-api';

// Thunk to authenticate/check session on page load (similar to main app's authenticate)
export const authenticatePortal = createAsyncThunk(
  'candidatePortal/authenticate',
  async (_, { dispatch }) => {
    try {
      const result = await dispatch(
        candidatePortalApi.endpoints.getSession.initiate(undefined),
      ).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  },
);

const initialState = {
  // Authentication state
  isAuthenticated: false,
  candidateAuthId: null,
  email: null,
  hasPassword: false,
  checked: false, // Flag to track if auth check has completed (like main app)

  // Context state (which candidate/company is selected)
  selectedCandidateId: null,
  selectedCompanyId: null,

  // Linked candidates (for users with multiple)
  linkedCandidates: [],

  // Invitation context (from JWT link)
  invitationContext: null,

  // UI state
  isLoading: false,
  error: null,
};

const candidatePortalSlice = createSlice({
  name: 'candidatePortal',
  initialState,
  reducers: {
    /**
     * Set invitation context from decoded JWT
     */
    setInvitationContext: (state, action) => {
      state.invitationContext = action.payload;
    },

    /**
     * Set authentication state after successful login
     */
    setAuthenticated: (state, action) => {
      const {
        candidateAuthId,
        email,
        hasPassword,
        linkedCandidates,
        selectedCandidateId,
        selectedCompanyId,
      } = action.payload;

      state.isAuthenticated = true;
      state.candidateAuthId = candidateAuthId;
      state.email = email;
      state.hasPassword = hasPassword;
      state.linkedCandidates = linkedCandidates || [];
      state.selectedCandidateId = selectedCandidateId || null;
      state.selectedCompanyId = selectedCompanyId || null;
      state.checked = true;
      state.error = null;
    },

    /**
     * Update selected context
     */
    setSelectedContext: (state, action) => {
      state.selectedCandidateId = action.payload.candidateId;
      state.selectedCompanyId = action.payload.companyId;
    },

    /**
     * Update password flag
     */
    setHasPassword: (state, action) => {
      state.hasPassword = action.payload;
    },

    /**
     * Set loading state
     */
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    /**
     * Set error state
     */
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    /**
     * Clear error
     */
    clearError: state => {
      state.error = null;
    },

    /**
     * Logout - clear all state
     */
    logout: state => {
      clearPortalToken();
      return { ...initialState };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(authenticatePortal.fulfilled, (state, { payload }) => {
        state.isAuthenticated = true;
        state.candidateAuthId = payload.candidate_auth_id;
        state.email = payload.email;
        state.hasPassword = payload.has_password;
        state.linkedCandidates = payload.linked_candidates || [];
        state.checked = true;
        state.error = null;
      })
      .addCase(authenticatePortal.rejected, state => {
        state.checked = true;
        state.isAuthenticated = false;
      });
  },
});

export const {
  setInvitationContext,
  setAuthenticated,
  setSelectedContext,
  setHasPassword,
  setLoading,
  setError,
  clearError,
  logout,
} = candidatePortalSlice.actions;

export default candidatePortalSlice.reducer;

// Selectors
export const selectPortalAuth = state => ({
  isAuthenticated: state.candidatePortal.isAuthenticated,
  candidateAuthId: state.candidatePortal.candidateAuthId,
  email: state.candidatePortal.email,
  hasPassword: state.candidatePortal.hasPassword,
  checked: state.candidatePortal.checked,
});

export const selectPortalContext = state => ({
  selectedCandidateId: state.candidatePortal.selectedCandidateId,
  selectedCompanyId: state.candidatePortal.selectedCompanyId,
});

export const selectLinkedCandidates = state => state.candidatePortal.linkedCandidates;

export const selectInvitationContext = state => state.candidatePortal.invitationContext;

export const selectPortalLoading = state => state.candidatePortal.isLoading;

export const selectPortalError = state => state.candidatePortal.error;

export const selectHasMultipleCandidates = state =>
  state.candidatePortal.linkedCandidates.length > 1;

export const selectNeedsContextSelection = state =>
  state.candidatePortal.isAuthenticated &&
  !state.candidatePortal.selectedCandidateId &&
  state.candidatePortal.linkedCandidates.length > 1;
